/**
 * Session bootstrap data — the active user's identity, company, and
 * enabled module codes. Powers the sidebar and the ``<RequiresModule>``
 * route gate. See ``apps.accounts.views.MeView`` + ``BIOMETRICS_SPEC.md``
 * §2.4 for the backend contract.
 */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { isAuthenticated } from "@/lib/auth";

export interface MeCompany {
  id: string;
  display_name: string;
  legal_name: string;
  country_code: string;
}

export interface Me {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  is_superuser: boolean;
  /** Dinnox-IT platform super admin — gates the vendor control plane. */
  is_super_admin?: boolean;
  is_staff: boolean;
  /** True on single-client deployments locked to one company — the SPA hides
   *  the "New company" control. Optional so older payloads stay valid. */
  single_company_mode?: boolean;
  employee_id: string | null;
  company: MeCompany | null;
  enabled_modules: string[];
  /** Flat ``"module_code.feature_code"`` keys for enabled functions.
   *  Optional so older cached payloads (pre-entitlements) stay valid. */
  enabled_features?: string[];
  roles: string[];
  /** Per-user AI capability — true when the user holds the "AI User" role
   *  (or is a platform super admin). Gates My Workspace → Assistant; the
   *  copilot API enforces the same server-side. Optional so older cached
   *  payloads stay valid (absent ⇒ fail closed). */
  can_use_ai_assistant?: boolean;
  /** Present (non-null) only when the session is an active impersonation —
   *  drives the "acting as" banner. */
  impersonation?: {
    impersonator_id: string;
    impersonator_name: string;
    session_id: string;
  } | null;
}

/** Canonical module-code constants. Mirror ``apps.organization.models.ModuleCode``. */
export const ModuleCode = {
  // Core business modules (on by default)
  EMPLOYEES: "employees",
  LIFECYCLE: "lifecycle",
  LEAVE: "leave",
  ATTENDANCE: "attendance",
  COMPENSATION: "compensation",
  PAYROLL: "payroll",
  EXPENSES: "expenses",
  RECRUITMENT: "recruitment",
  PERFORMANCE: "performance",
  EVENTS: "events",
  WORKFLOWS: "workflows",
  NOTIFICATIONS: "notifications",
  REPORTS: "reports",
  ACCOUNTING: "accounting",
  PH_COMPLIANCE: "ph_compliance",
  // Premium bolt-ons (off by default)
  BIOMETRIC_ATTENDANCE: "biometric_attendance",
  COPILOT: "copilot",
} as const;

export type ModuleCodeValue = (typeof ModuleCode)[keyof typeof ModuleCode];

/** Flat key used in ``Me.enabled_features``. Mirror
 *  ``apps.organization.entitlements.feature_key``. */
export function featureKey(moduleCode: string, featureCode: string): string {
  return `${moduleCode}.${featureCode}`;
}

// Dedicated key for the canonical session payload (/api/v1/me/). It must NOT
// be ["me"] — several components run a *different* query under ["me"] that
// hits /accounts/users/me/ (the trimmed self-profile serializer, which omits
// is_staff/is_superuser/enabled_modules). Sharing the key let those responses
// clobber this cache, intermittently wiping role/module flags (e.g. the
// employee-detail "Reset password" button vanishing).
export const ME_QUERY_KEY = ["session-me"] as const;

/**
 * Re-scope the query cache after an auth-context switch (impersonation
 * start/stop) and return once ``/me/`` reflects the new identity.
 *
 * Why not ``queryClient.clear()`` + refetch? ``clear()`` DESTROYS the
 * ``/me/`` query and detaches its already-mounted observers — the
 * ``ImpersonationBanner`` / ``ImpersonationFrame`` living in ``AppLayout``,
 * which stays mounted across the admin-console → app navigation. A subsequent
 * fetch lands in a brand-new query instance those observers never subscribed
 * to, so they only pick it up on an incidental re-render (the banner has its
 * own timer state and happens to; the stateless frame does not) — the classic
 * "only updates after a manual reload" bug, worse still asymmetric between the
 * two.
 *
 * Instead: drop every OTHER query (so no stale cross-tenant data survives the
 * switch) but keep the ``/me/`` query alive, then invalidate it. Invalidation
 * refetches the still-subscribed query under the now-current token and
 * notifies its mounted observers directly — no dependence on render timing, so
 * banner and frame flip together, immediately.
 */
export async function reloadMeAfterAuthSwitch(
  queryClient: QueryClient,
): Promise<void> {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
  });
  await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

export function useMe() {
  return useQuery<Me>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.get<Me>("/me/"),
    // 5-minute cache — module flips are operationally rare; refetch on
    // window focus catches the case where an admin toggles a module
    // mid-session.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Only fire when there's actually a token to authenticate with;
    // avoids a 401 on the login screen.
    enabled: isAuthenticated(),
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401/403 — those are auth states, not transient
      // failures.
      const status = (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

/**
 * True iff ``code`` is enabled for the active tenant.
 *
 * Returns ``false`` while the ``/me/`` query is in flight so callers
 * fail closed — never render a gated surface based on stale module
 * state from a different tenant.
 */
export function useIsModuleEnabled(code: ModuleCodeValue | string): boolean {
  const { data } = useMe();
  // Fail closed: missing/absent module list → not enabled (also guards
  // partial cache rehydration and lean test fixtures).
  return data?.enabled_modules?.includes(code) ?? false;
}

/**
 * True iff the function ``featureCode`` within ``moduleCode`` is enabled for
 * the active tenant. Functions default ON server-side, so this returns false
 * only where a plan or per-client override switched the function off (or while
 * ``/me/`` is in flight — fail closed). Mirrors the backend
 * ``requires_feature`` gate.
 */
export function useIsFeatureEnabled(
  moduleCode: ModuleCodeValue | string,
  featureCode: string,
): boolean {
  const { data } = useMe();
  return data?.enabled_features?.includes(featureKey(moduleCode, featureCode)) ?? false;
}

/**
 * True iff the AI Assistant is usable by THIS user: the copilot module is
 * enabled for the tenant AND the user holds the per-user AI capability
 * (the "AI User" role — assigned under Settings → Roles & permissions).
 * Fails closed while ``/me/`` is in flight.
 */
export function useCanUseAiAssistant(): boolean {
  const { data } = useMe();
  return Boolean(
    data?.enabled_modules?.includes(ModuleCode.COPILOT) &&
      data?.can_use_ai_assistant,
  );
}

/** True for a Dinnox-IT platform super admin (gates the vendor control
 *  plane). Mirrors the backend ``is_platform_super_admin``: the explicit
 *  flag OR Django's ``is_superuser``. */
export function useIsSuperAdmin(): boolean {
  const { data } = useMe();
  return Boolean(data?.is_super_admin || data?.is_superuser);
}

/** Imperative invalidator — call after enabling/disabling a module
 *  from settings, or on receipt of a 403 ``module_disabled`` response. */
export function useInvalidateMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}
